/**
 * @fileoverview O passo do WhatsApp na esteira de cadastro: conectar o número
 * da empresa agora (com o modal de conexão de sempre e o QR code, que abre
 * sozinho quando a conexão nova aparece) ou depois. As conexões da conta vêm do
 * WhatsAppsContext, ao vivo: o estado muda na tela quando o QR é lido.
 * Conectar pede connections.manage, como na página de Conexões, e o e-mail
 * confirmado: antes dele, o passo pede a confirmação (e dá para fazer depois).
 */

import React, { useContext, useEffect, useRef, useState } from "react";

import { useAuthFormStyles } from "../AuthLayout/formStyles";
import WhatsAppModal from "../WhatsAppModal";
import QrcodeModal from "../QrcodeModal";
import PasskeyModal from "../PasskeyModal";
import { allows } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import usePermission from "../../hooks/usePermission";
import { useEmailConfirmation } from "../../hooks/useEmailConfirmation";
import { i18n } from "../../translate/i18n";
import useSetupStyles from "./styles";

const t = (key, options) => i18n.t(`companySetup.whatsapp.${key}`, options);
const WAITING = ["qrcode", "OPENING", "PAIRING"];

const statusOf = (status) => {
  if (status === "CONNECTED") return "ok";
  return WAITING.includes(status) ? "wait" : "bad";
};

const WhatsAppStep = ({ emailVerified, onEmailConfirmed, onNext }) => {
  const form = useAuthFormStyles();
  const classes = useSetupStyles();
  const { user } = useContext(AuthContext);
  const { whatsApps = [] } = useContext(WhatsAppsContext) || {};
  const { can } = usePermission();
  const canConnect = can("connections.manage", allows(user, "connections-page:addConnection"));
  const [modalOpen, setModalOpen] = useState(false);
  const [qrFor, setQrFor] = useState(null);
  const [passkey, setPasskey] = useState(null);
  const needsEmail = canConnect && !emailVerified;
  const confirmation = useEmailConfirmation({ email: user.email, onConfirmed: onEmailConfirmed, active: needsEmail });
  // As conexões antes de abrir o modal: a que aparecer depois é a nova, e o QR dela abre sozinho.
  const knownIds = useRef(null);

  useEffect(() => {
    if (!knownIds.current) return;
    const created = whatsApps.find((whatsApp) => !knownIds.current.includes(whatsApp.id));
    if (!created) return;
    knownIds.current = null;
    if (created.status !== "CONNECTED") setQrFor(created.id);
  }, [whatsApps]);

  const openModal = () => {
    knownIds.current = whatsApps.map((whatsApp) => whatsApp.id);
    setModalOpen(true);
  };

  const anyConnected = whatsApps.some((whatsApp) => whatsApp.status === "CONNECTED");

  return (
    <div className={`${classes.step} ${form.view}`}>
      <h1 className={form.title}>{t("title")}</h1>
      <p className={form.subtitle} style={{ marginBottom: 0 }}>{t("subtitle")}</p>

      {whatsApps.length > 0 && (
        <ul className={classes.list}>
          {whatsApps.map((whatsApp) => {
            const tone = statusOf(whatsApp.status);
            const label = i18n.exists(`companySetup.whatsapp.status.${whatsApp.status}`) ? t(`status.${whatsApp.status}`) : t("status.DISCONNECTED");
            return (
              <li key={whatsApp.id} className={classes.listItem}>
                <div className={classes.listMain}>
                  <div className={classes.listTitle}>{whatsApp.name}</div>
                  {whatsApp.number && <div className={classes.listMeta}>{whatsApp.number}</div>}
                </div>
                <span className={`${classes.status} ${tone === "ok" ? classes.statusOk : tone === "wait" ? classes.statusWait : classes.statusBad}`}>
                  <span className={classes.statusDot} aria-hidden="true" />
                  {label}
                </span>
                {canConnect && whatsApp.status === "qrcode" && (
                  <button type="button" className={classes.rowAction} onClick={() => setQrFor(whatsApp.id)}>
                    {t("showQr")}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {whatsApps.some((whatsApp) => whatsApp.status === "qrcode") && <p className={classes.howTo}>{t("info")}</p>}
      {!canConnect && <p className={classes.howTo}>{t("noPermission")}</p>}
      {needsEmail && (
        <div className={form.notice} style={{ marginTop: 20 }} role="status">
          {confirmation.notice || t("verifyFirst", { email: user.email })}{" "}
          <button type="button" className={form.link} onClick={confirmation.resend} disabled={confirmation.sending}>
            {t("resend")}
          </button>
        </div>
      )}
      {needsEmail && confirmation.error && (
        <div className={`${form.notice} ${form.noticeBad}`} style={{ marginTop: 12 }} role="alert">{confirmation.error}</div>
      )}

      <div className={classes.actions}>
        {anyConnected ? (
          <button type="button" className={`${form.button} ${classes.actionButton}`} onClick={onNext}>
            {t("next")}
          </button>
        ) : needsEmail ? (
          <button type="button" className={`${form.button} ${classes.actionButton}`} onClick={confirmation.check} disabled={confirmation.checking}>
            {t("alreadyConfirmed")}
          </button>
        ) : (
          canConnect && (
            <button type="button" className={`${form.button} ${classes.actionButton}`} onClick={openModal}>
              {whatsApps.length > 0 ? t("connectAnother") : t("connect")}
            </button>
          )
        )}
        {!anyConnected && (
          <button type="button" className={`${form.secondary} ${classes.actionSecondary}`} onClick={onNext}>
            {t("later")}
          </button>
        )}
      </div>

      <WhatsAppModal open={modalOpen} onClose={() => setModalOpen(false)} whatsAppId={null} />
      <QrcodeModal
        open={!!qrFor}
        onClose={() => setQrFor(null)}
        whatsAppId={qrFor}
        onPasskeyRequired={(status) => {
          setPasskey({ id: qrFor, status: status || "PASSKEY_REQUIRED" });
          setQrFor(null);
        }}
      />
      <PasskeyModal open={!!passkey} onClose={() => setPasskey(null)} whatsAppId={passkey?.id || null} initialStatus={passkey?.status} />
    </div>
  );
};

export default WhatsAppStep;
